import { useAuth } from "@/context/auth";
import OtpSender from "@/modules/auth/verifyCode";
import useLoginByOtpCode from "@/services/auth/hooks/useLoginByOtpCode";
import useLoginByPassword from "@/services/auth/hooks/useLoginByPassword";
import useSignUpVerify from "@/services/auth/hooks/useSignUpVeriy";
import useVerifyOtp from "@/services/auth/hooks/useVerifyOtp";
import {
  SignInPayload,
  TokenDTo,
  UserVerifyOtpPayload,
} from "@/services/auth/type";
import {
  Anchor,
  Button,
  FocusTrap,
  Grid,
  Group,
  Loader,
  PasswordInput,
  PinInput,
  Stack,
  Text,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { IconChevronLeft } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

declare global {
  interface CredentialRequestOptions {
    otp?: { transport: string[] };
  }
  interface OTPCredential extends Credential {
    code: string;
  }
}

const signUpSchema = Yup.object({
  password: Yup.string()
    .required("پسورد را وارد نمایید")
    .matches(
      /^[a-zA-Z0-9!@#$%^&*()_+=-]*$/,
      "پسورد نمی‌تواند شامل حروف و اعداد فارسی باشد"
    ),
});

/** Converts Arabic (٠-٩) and Persian (۰-۹) digits to English (0-9). */
function toEnglishDigits(str: string): string {
  if (!str || typeof str !== "string") return str;
  return str.replace(/[٠-٩۰-۹]/g, (d) =>
    String("٠١٢٣٤٥٦٧٨٩۰۱۲۳۴۵۶۷۸۹".indexOf(d) % 10)
  );
}

function Verification() {
  const [loginType, setLoginType] = useState<"Password" | "Otp">("Password");
  const otpInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const {
    phoneNumber,
    isSignUp,
    firstName,
    lastName,
    imei,
    mobileNumber,
    password,
    refCode,
    merchant,
    merchantUserType,
  } = location.state;

  const { mutate: verifyOtp, isLoading } = useVerifyOtp();
  const { login } = useAuth();
  const { mutate, isLoading: signInByPasswordLoading } = useLoginByPassword();
  const { mutate: setOtp } = useLoginByOtpCode();
  const { mutate: signUpVerify, isLoading: loadingVerify } = useSignUpVerify();
  const navigate = useNavigate();
  // const os = useOs();
  const form = useForm<UserVerifyOtpPayload | SignInPayload>({
    validate: yupResolver(signUpSchema),
    initialValues: {
      otpCode: "",
      username: phoneNumber || mobileNumber,
      password: "",
    },
  });

  const onSuccessMutate = (data: TokenDTo) => {
    login(data, undefined, merchant);
  };

  useEffect(() => {
    if ((form.values as UserVerifyOtpPayload)?.otpCode?.length === 4) {
      if (!isSignUp) {
        verifyOtp(
          {
            otpCode: toEnglishDigits((form.values as UserVerifyOtpPayload)?.otpCode ?? ""),
            username: phoneNumber || mobileNumber,
          },
          {
            onSuccess(data) {
              onSuccessMutate(data);
            },
          }
        );
      } else {
        signUpVerify(
          {
            firstName,
            lastName,
            mobileNumber: phoneNumber || mobileNumber,
            password,
            otpCode: toEnglishDigits((form?.values as UserVerifyOtpPayload).otpCode ?? ""),
            imei,
            referralCode: refCode,
            merchantUserType,
          },
          {
            onSuccess(data) {
              onSuccessMutate(data);
            },
          }
        );
      }
    }
  }, [form.values, phoneNumber, mobileNumber, verifyOtp, isSignUp]);

  useEffect(() => {
    if (isSignUp) setLoginType("Otp");
  }, [isSignUp]);

  useEffect(() => {
    if (loginType === "Otp" && otpInputRef.current) {
      if ("OTPCredential" in window) {
        navigator.credentials
          .get({
            otp: { transport: ["sms"] },
          })
          .then((otp) => {
            if (otp && "code" in otp) {
              const code = (otp as OTPCredential).code;
              form.setFieldValue("otpCode", code);
              if (code.length === 4) {
                if (!isSignUp) {
                  verifyOtp(
                    {
                      otpCode: toEnglishDigits(code),
                      username: phoneNumber || mobileNumber,
                    },
                    {
                      onSuccess(data) {
                        onSuccessMutate(data);
                      },
                    }
                  );
                } else {
                  signUpVerify(
                    {
                      firstName,
                      lastName,
                      mobileNumber: phoneNumber || mobileNumber,
                      password,
                      otpCode: toEnglishDigits(code),
                      imei,
                      referralCode: refCode,
                    },
                    {
                      onSuccess(data) {
                        onSuccessMutate(data);
                      },
                    }
                  );
                }
              }
            }
          })
          .catch((err) => {
            console.log("Error reading SMS:", err);
          });
      }
    }
  }, [loginType]);

  const submit = (values: SignInPayload) => {
    mutate(
      {
        password: values?.password,
        username: phoneNumber || mobileNumber,
      },
      {
        onSuccess(data) {
          onSuccessMutate(data);
        },
      }
    );
  };

  return (
    <Stack h={300} justify="center">
      {loginType === "Otp" ? (
        <form>
          <Grid>
            <Grid.Col span={{ base: 12, md: 12, lg: 12 }}>
              <Stack align="center" justify="center" pos="relative">
                <Text size="sm">
                  کد ارسال شده به {phoneNumber || mobileNumber} را وارد نمایید
                </Text>
                {/* {os === "ios" ? (
                  <Stack w="100%">
                    <Input
                      styles={{
                        input: {
                          textAlign: "center",
                        },
                      }}
                      size="lg"
                      ta="center"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={4}
                      placeholder="- - - -"
                      disabled={isLoading}
                      {...form.getInputProps("otpCode")}
                    />
                  </Stack>
                ) : (
                  <PinInput
                    size="lg"
                    inputType="tel"
                    inputMode="numeric"
                    placeholder=""
                    disabled={isLoading}
                    dir="ltr"
                    ref={otpInputRef}
                    {...form.getInputProps("otpCode")}
                  />
                )} */}
                <FocusTrap active={true}>
                  <PinInput
                    size="lg"
                    inputType="tel"
                    inputMode="numeric"
                    placeholder=""
                    disabled={isLoading}
                    oneTimeCode
                    dir="ltr"
                    ref={otpInputRef}
                    {...form.getInputProps("otpCode")}
                  />
                </FocusTrap>

                {isLoading ||
                  (loadingVerify && (
                    <Stack
                      justify="center"
                      align="center"
                      pos="absolute"
                      bottom={-27}
                    >
                      <Loader type="dots" size="xs" />
                    </Stack>
                  ))}
              </Stack>
            </Grid.Col>
          </Grid>
        </form>
      ) : (
        <form onSubmit={form.onSubmit(submit)}>
          <Stack>
            <Text size="sm">کلمه عبور را وارد نمایید</Text>
            <PasswordInput
              size="lg"
              label=""
              styles={{
                root: {
                  direction: "ltr",
                },
                error: {
                  direction: "rtl",
                  fontSize: "0.8rem",
                },
              }}
              {...form.getInputProps("password")}
            />
            <Button type="submit" size="lg" loading={signInByPasswordLoading}>
              تایید
            </Button>
          </Stack>
        </form>
      )}
      {loginType === "Otp" && (
        <OtpSender resendType={isSignUp ? "singUp" : "login"} />
      )}
      {!isSignUp && (
        <Stack justify="center" align="center" mt="lg">
          <Group justify="space-between" w="100%" align="start">
            {loginType === "Otp" ? (
              <Text size="xs">
                <Anchor
                  underline="always"
                  onClick={() => {
                    setLoginType("Password");
                    form.setValues({
                      otpCode: "",
                    });
                  }}
                >
                  <Group gap="0">
                    <Text size="xs">ورود با رمز عبور</Text>
                    <IconChevronLeft size={15} />
                  </Group>
                </Anchor>
              </Text>
            ) : (
              <Stack>
                <Text size="xs">
                  <Anchor
                    underline="always"
                    onClick={() => {
                      setLoginType("Otp");
                      setOtp({
                        username: phoneNumber || mobileNumber,
                      });
                    }}
                  >
                    <Group gap="0">
                      <Text size="xs">ورود با رمز یک‌بار‌مصرف</Text>
                      <IconChevronLeft size={15} />
                    </Group>
                  </Anchor>
                </Text>
                <Text size="xs">
                  <Anchor
                    underline="always"
                    onClick={() =>
                      navigate("/auth/forgetPasswordPhoneNumber", {
                        state: {
                          phoneNumber: mobileNumber || phoneNumber,
                        },
                      })
                    }
                  >
                    <Group gap="0">
                      <Text size="xs"> فراموشی رمز عبور </Text>
                      <IconChevronLeft size={15} />
                    </Group>
                  </Anchor>
                </Text>
              </Stack>
            )}

            <Text size="xs">
              <Link to="/auth/login" style={{ textDecoration: "none" }}>
                <Anchor underline="always">تغییر شماره</Anchor>
              </Link>
            </Text>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

export default Verification;
